import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { catchError, forkJoin, of } from 'rxjs';
import { DsmDealOption, DsmDealOptionProduct } from '../../../models/dsm-deal-options-view.models';
import { DsmApsAddendumProductPreviewComponent } from '../dsm-aps-addendum-product-preview/dsm-aps-addendum-product-preview.component';
import { DsmAddOptionModalComponent } from '../dsm-add-option-modal/dsm-add-option-modal.component';
import { DsmRoomReplacementRulesService } from '../../../services/dsm-room-replacement-rules.service';
import { DividerModule } from '@openng/optimus-ui/divider';

type DsmDealOptionProductWithExpandableView = DsmDealOptionProduct & { isExpanded: boolean };

@Component({
  selector: 'dsm-aps-addendum-options',
  standalone: true,
  imports: [
    NgClass,
    DsmApsAddendumProductPreviewComponent,
    DsmAddOptionModalComponent,
    ButtonModule,
    DrawerModule,
    DialogModule,
    DividerModule
  ],
  templateUrl: './dsm-aps-addendum-options.component.html',
  styleUrl: './dsm-aps-addendum-options.component.scss'
})
export class DsmApsAddendumOptionsComponent {
  private readonly roomReplacementRulesService = inject(DsmRoomReplacementRulesService);

  readonly dealAddendum = input.required<DsmDealOption>();
  readonly projectName = input<string | null>();
  readonly lotNo = input<string | null>();
  readonly modelId = input.required<string>();
  readonly elevationId = input.required<string>();
  readonly modelName = input<string | null>();
  readonly elevationLabel = input<string | null>();
  readonly buyerName = input<string | null>(null);
  readonly selectedProductId = input<string | null>(null);
  /** Room Replacement Rules: show the "Mismatch Locations" badge on these product ids. Review List only. */
  readonly mismatchedProductIds = input<ReadonlySet<string>>(new Set());
  /** Room Replacement Rules: productIds (catalog, not line ids) of trigger options whose rule has run. */
  readonly executedTriggerProductIds = input<ReadonlySet<string>>(new Set());
  /** Review List hides the Add Option affordance — it only exists to fix existing selections. */
  readonly showAddOption = input<boolean>(true);
  readonly isReadonly = input<boolean>(false);
  readonly isLocked = computed(() => this.dealAddendum().isLocked || this.dealAddendum().isCancelled);

  onChanges = output<void>();
  onProductSelected = output<string>();
  onKitProductSelected = output<string>();

  addOptionDialogVisible = signal(false);
  filtersVisible = signal(true);

  readonly addOptionModal = viewChild(DsmAddOptionModalComponent);
  readonly activeFilterCount = computed(() => this.addOptionModal()?.activeFilterCount() ?? 0);

  readonly products = computed<DsmDealOptionProductWithExpandableView[]>(() =>
    this.dealAddendum().products.map(
      product => ({ ...product, isExpanded: false }) as DsmDealOptionProductWithExpandableView
    )
  );

  private readonly expandSelectedKitParent = effect(() => {
    const selectedId = this.selectedProductId();
    if (!selectedId) return;
    const parent = this.products().find(product => product.linkedProducts?.some(linked => linked.id === selectedId));
    if (parent) parent.isExpanded = true;
  });

  readonly addedProductIds = computed(() => this.products().map(p => p.productId));

  readonly headerSummary = computed(() => {
    const parts: string[] = [];
    if (this.buyerName()) parts.push(this.buyerName()!);
    if (this.projectName()) parts.push(this.projectName()!);
    if (this.dealAddendum().builderReference?.reference) parts.push(this.dealAddendum().builderReference.reference);
    if (this.lotNo()) parts.push(`Lot: ${this.lotNo()}`);
    if (this.modelName() || this.elevationLabel()) {
      parts.push(`Model/Elevation: ${this.modelName() || '—'}/${this.elevationLabel() || '—'}`);
    }
    return parts.join(' | ');
  });

  onProductSelect(productId: string): void {
    if (this.isReadonly()) return;
    this.onProductSelected.emit(productId);
  }

  onKitProductSelect(productId: string): void {
    if (this.isReadonly()) return;
    this.onKitProductSelected.emit(productId);
  }

  toggleFilters(): void {
    this.filtersVisible.update(v => !v);
  }

  openAddOptionDialog(): void {
    if (this.isReadonly()) return;
    if (!this.isLocked()) {
      this.filtersVisible.set(true);
      this.addOptionDialogVisible.set(true);
    }
  }

  /** Room Replacement Rules: set once the drawer has closed, if any just-added product is an
   *  active rule's trigger. Shown as a blocking dialog with an OK button so the consultant
   *  has to acknowledge it, not a toast they could miss. */
  readonly planChangeWarningMessage = signal<string | null>(null);

  onOptionsSaved(addedProductIds: string[]): void {
    this.addOptionDialogVisible.set(false);
    this.onChanges.emit();
    this.checkForPlanChangingOption(addedProductIds);
  }

  private checkForPlanChangingOption(productIds: string[]): void {
    if (!productIds.length) return;

    forkJoin(
      productIds.map(productId =>
        this.roomReplacementRulesService
          .validateRule(this.modelId(), this.elevationId(), productId, false)
          .pipe(catchError(() => of(null)))
      )
    ).subscribe({
      next: validations => {
        const trigger = validations.find(validation => !!validation?.ruleId);
        if (trigger) {
          this.planChangeWarningMessage.set(
            'An active room replacement rule exists for this floor plan on selected model and elevation.'
          );
        }
      }
    });
  }

  acknowledgePlanChangeWarning(): void {
    this.planChangeWarningMessage.set(null);
  }
}
